import { useEffect, useMemo, useState } from 'react';

import { ProductSearchItem } from '@telialt/core-search/src/model';
import { isDesktopRes } from '@telialt/utils/src/responsive';

import {
    PRODUCT_COMPARE_MAX_DESKTOP_COUNT,
    PRODUCT_COMPARE_MAX_MOBILE_COUNT,
    PRODUCT_COMPARE_STORAGE_KEY,
} from '../constants/constants';
import { ProductsCompareProduct, ProductsCompareStorageItem } from '../models';

const COMPARE_EXPIRE_TIME = 15 * 60 * 1000;

const getAllCompareStorageItems = () => {
    let allCompareStorageItems: ProductsCompareStorageItem[] = [];
    try {
        const rawComparedProducts = localStorage.getItem(PRODUCT_COMPARE_STORAGE_KEY);
        if (rawComparedProducts) {
            allCompareStorageItems = JSON.parse(rawComparedProducts);
        }
    } catch {
        return [];
    }

    if (!allCompareStorageItems || !Array.isArray(allCompareStorageItems)) {
        return [];
    }

    return allCompareStorageItems.filter((item) => Date.now() - item.lastUpdateAt < COMPARE_EXPIRE_TIME);
};

const setAllCompareStorageItems = (items: ProductsCompareStorageItem[]) => {
    try {
        localStorage.setItem(PRODUCT_COMPARE_STORAGE_KEY, JSON.stringify(items));
    } catch {
        // noop
    }
};

const retrieveCategoryComparedProducts = (categoryPath: string) => {
    const allCompareStorageItems = getAllCompareStorageItems();
    if (!Array.isArray(allCompareStorageItems)) {
        return [];
    }

    return allCompareStorageItems.find((item) => item.categoryPath === categoryPath)?.products || [];
};

const storeCategoryComparedProducts = (categoryPath: string, products: ProductsCompareProduct[]) => {
    const allCompareStorageItems = getAllCompareStorageItems();

    const updatedCompareStorageItems: ProductsCompareStorageItem[] = [
        ...allCompareStorageItems.filter((item) => item.categoryPath !== categoryPath),
        { categoryPath, lastUpdateAt: Date.now(), products },
    ];

    setAllCompareStorageItems(updatedCompareStorageItems);
};

export const useProductsCompare = (categoryPath: string) => {
    const [comparedProducts, setComparedProducts] = useState<ProductsCompareProduct[]>([]);
    const [compareProductsLimit, setCompareProductsLimit] = useState(PRODUCT_COMPARE_MAX_DESKTOP_COUNT);
    const [isComparedProductsLoading, setIsComparedProductsLoading] = useState(true);

    useEffect(() => {
        setComparedProducts(retrieveCategoryComparedProducts(categoryPath));
        setIsComparedProductsLoading(false);
    }, [categoryPath]);

    useEffect(() => {
        const updateProductsLimit = () => {
            setCompareProductsLimit(
                isDesktopRes() ? PRODUCT_COMPARE_MAX_DESKTOP_COUNT : PRODUCT_COMPARE_MAX_MOBILE_COUNT,
            );
        };
        updateProductsLimit();
        window.addEventListener('resize', updateProductsLimit);

        return () => window.removeEventListener('resize', updateProductsLimit);
    }, []);

    const addComparedProduct = (product: ProductSearchItem) => {
        if (
            comparedProducts.some((item) => item.id === product.id) ||
            comparedProducts.length >= compareProductsLimit
        ) {
            return;
        }

        const updatedComparedProducts: ProductsCompareProduct[] = [
            ...comparedProducts,
            { id: product.id, image: product.img100x, name: product.name, url: product.url },
        ];
        setComparedProducts(updatedComparedProducts);
        storeCategoryComparedProducts(categoryPath, updatedComparedProducts);
    };

    const removeComparedProduct = (productId: string) => {
        const updatedComparedProducts = comparedProducts.filter((item) => item.id !== productId);
        setComparedProducts(updatedComparedProducts);
        storeCategoryComparedProducts(categoryPath, updatedComparedProducts);
    };

    const removeAllComparedProducts = () => {
        setComparedProducts([]);
        storeCategoryComparedProducts(categoryPath, []);
    };

    useEffect(() => {
        if (compareProductsLimit < comparedProducts.length) {
            const updatedComparedProducts = comparedProducts.slice(0, compareProductsLimit);
            setComparedProducts(updatedComparedProducts);
            storeCategoryComparedProducts(categoryPath, updatedComparedProducts);
        }
    }, [categoryPath, compareProductsLimit, comparedProducts]);

    const limitedComparedProducts = useMemo(
        () => comparedProducts.slice(0, compareProductsLimit),
        [compareProductsLimit, comparedProducts],
    );

    return {
        isComparedProductsLoading,
        compareProductsLimit,
        comparedProducts: limitedComparedProducts,
        addComparedProduct,
        removeComparedProduct,
        removeAllComparedProducts,
    };
};
